import { Helmet } from 'react-helmet-async';
import { filter, toLower, includes } from 'lodash';
import { useState, useEffect } from 'react';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Box,
  FormControl,
  Select,
  TextField,
} from '@mui/material';
import {  useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
// components
import Iconify from '../components/iconify';
import CustomPopup from '../client/Components/CustomPopup';
import Scrollbar from '../components/scrollbar';
// sections
import { UserListToolbar } from '../sections/@dashboard/user';
// mock
import OrderProductListHead from '../sections/@dashboard/user/OrderProductListHead';
import {
  getCustomVendorOrdersCancelledMethod,
  getCustomVendorOrdersCompleteMethod,
  getCustomVendorOrdersConfirmMethod,
  getCustomVendorOrdersDeliverMethod,
  getCustomVendorOrdersMethod,
  getCustomVendorOrdersPendingMethod,
} from '../redux/slice/customVendorOrdersSlice';
import { IMAGE_BASE_URL } from '../redux/api/http-common';
import CustomVendorOrdersService from '../redux/api/CustomVendorOrdersService';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'first_name', label: 'Vendor Name', alignRight: false },
  { id: 'email', label: 'Vendor Email', alignRight: false },
  { id: 'titles', label: ' Product Name', alignRight: false },
  { id: 'sku', label: 'PPW NO.', alignRight: false },
  { id: 'prices', label: 'Basic Price', alignRight: false },
  { id: 'cartQuantity', label: 'Quantity', alignRight: false },
  { id: 'totalPrice', label: 'Total Price', alignRight: false },
  { id: 'customFields', label: 'Custom Fields', alignRight: false },
  { id: 'lamination', label: 'Lamination', alignRight: false },
  { id: 'mobileModel', label: 'Mobile Model', alignRight: false },
  { id: 'tissueFilling', label: 'Tissue Filling', alignRight: false },
  { id: 'files', label: 'File', alignRight: false },
  { id: 'images', label: 'Image', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'disputeDescription', label: 'Reason', alignRight: false },
  { id: 'action', label: 'Action', alignRight: false },
  { id: '', label: 'Product Detail', alignRight: false },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => {
      const lowerQuery = toLower(query);
      return (
        includes(toLower(_user?.vendorId?.first_name), lowerQuery) ||
        includes(toLower(_user?.vendorId?.last_name), lowerQuery) ||
        includes(toLower(_user?.vendorId?.email), lowerQuery)
      );
    });
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function CustomVendorOrdersPage() {
  const dispatch = useDispatch();

  const userData = useSelector((state) => state.user.userInfo);

  const [orderId, setOrderId] = useState(null);

  const navigate = useNavigate();

  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('asc');

  const [orderBy, setOrderBy] = useState('name');

  const [filterName, setFilterName] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [requestName, setRequestName] = useState('All');

  const [changeStatusMenu, setChangeStatusMenu] = useState(false);

  const [orderStatus, setOrderStatus] = useState(false);
  const [isDispute, setIsDispute] = useState(false);
  const [disputeDescription, setDisputeDescription] = useState('');
  const [ordersStatus, setOrdersStatus] = useState('');

  const data = useSelector((state) => state.customVendorOrders);

  useEffect(() => {
    if (requestName === 'All') {
      dispatch(getCustomVendorOrdersMethod());
    } else if (requestName === 'Pending') {
      dispatch(getCustomVendorOrdersPendingMethod());
    } else if (requestName === 'Confirm') {
      dispatch(getCustomVendorOrdersConfirmMethod());
    } else if (requestName === 'Completed') {
      dispatch(getCustomVendorOrdersCompleteMethod());
    } else if (requestName === 'Delivered') {
      dispatch(getCustomVendorOrdersDeliverMethod());
    } else {
      dispatch(getCustomVendorOrdersCancelledMethod());
    }
  }, [requestName,dispatch]);

  const handleOpenChangeStatusMenu = (event, id, Status) => {
    setChangeStatusMenu(event.currentTarget);
    setOrderId(id);
    setOrderStatus(Status);
  };
  const handleCloseChangeStatusMenu = () => {
    setChangeStatusMenu(false);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const filteredUsers = applySortFilter(
    data.data !== null && data.data !== undefined ? data.data : [],
    getComparator(order, orderBy),
    filterName
  );

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, filteredUsers.length - page * rowsPerPage);

  const isNotFound = !filteredUsers.length && !!filterName;

  const handleChangeStatus = async (e) => {
    if (userData?.role === 'admin' && (e.target.value === 'Disputed' || e.target.value === 'Return')) {
      setOrdersStatus(e.target.value);
      setIsDispute(true);
      handleCloseChangeStatusMenu();
      return;
    }
    const data = {
      orderId,
      status: e.target.value,
    };

    await CustomVendorOrdersService.updateStatus(data)
      .then((res) => {
        console.warn('--------', res);
        toast.success(res?.data?.message);
        if (requestName === 'All') {
          dispatch(getCustomVendorOrdersMethod());
        } else if (requestName === 'Pending') {
          dispatch(getCustomVendorOrdersPendingMethod());
        } else if (requestName === 'Confirm') {
          dispatch(getCustomVendorOrdersConfirmMethod());
        } else if (requestName === 'Completed') {
          dispatch(getCustomVendorOrdersCompleteMethod());
        } else if (requestName === 'Delivered') {
          dispatch(getCustomVendorOrdersDeliverMethod());
        }
        handleCloseChangeStatusMenu();
        setOrderId(null);
      })
      .catch((error) => {
        toast.error(error.response.data.message);
        throw error;
      });
  };

  const handleDisputed = async () => {
    const data = {
      orderId,
      status: ordersStatus,
      disputeDescription,
    };
    await CustomVendorOrdersService.updateStatus(data)
      .then((res) => {
        console.warn('--------', res);
        setIsDispute(false);
        toast.success(res?.data?.message);
        if (requestName === 'All') {
          dispatch(getCustomVendorOrdersMethod());
        } else if (requestName === 'Pending') {
          dispatch(getCustomVendorOrdersPendingMethod());
        } else if (requestName === 'Confirm') {
          dispatch(getCustomVendorOrdersConfirmMethod());
        } else if (requestName === 'Completed') {
          dispatch(getCustomVendorOrdersCompleteMethod());
        } else if (requestName === 'Delivered') {
          dispatch(getCustomVendorOrdersDeliverMethod());
        }
        handleCloseChangeStatusMenu();
        setOrderId(null);
      })
      .catch((error) => {
        toast.error(error.response.data.message);
        throw error;
      });
  };

  function camelToSentence(inputStr) {
    // Use regular expression to find camel case
    const words = inputStr.match(/[A-Za-z0-9]+/g);

    // Capitalize the first word and join with spaces
    const result = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

    return result;
  }

  return (
    <>
      <Helmet>
        <title>Custom Vendor Product Orders | PakPrint Wishes </title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            {requestName} Custom Vendor Product Orders
          </Typography>
          <Box>
            <Button
              variant="contained"
              sx={{ margin: '5px' }}
              onClick={() => {
                setRequestName('All');
                dispatch(getCustomVendorOrdersMethod());
              }}
            >
              All Orders
            </Button>
            <Button
              sx={{ margin: '5px' }}
              variant="contained"
              onClick={() => {
                setRequestName('Pending');
                dispatch(getCustomVendorOrdersPendingMethod());
              }}
            >
              Pending Orders
            </Button>
            <Button
              sx={{ margin: '5px' }}
              variant="contained"
              onClick={() => {
                setRequestName('Confirm');
                dispatch(getCustomVendorOrdersConfirmMethod());
              }}
            >
              Confirm Orders
            </Button>
            <Button
              sx={{ margin: '5px' }}
              variant="contained"
              onClick={() => {
                setRequestName('Completed');
                dispatch(getCustomVendorOrdersCompleteMethod());
              }}
            >
              Completed Orders
            </Button>
            <Button
              sx={{ margin: '5px' }}
              variant="contained"
              onClick={() => {
                setRequestName('Cancelled');
                dispatch(getCustomVendorOrdersCancelledMethod());
              }}
            >
              Cancelled Orders
            </Button>
            <Button
              sx={{ margin: '5px' }}
              variant="contained"
              onClick={() => {
                setRequestName('Delivered');
                dispatch(getCustomVendorOrdersDeliverMethod());
              }}
            >
              Deliverd Orders
            </Button>
          </Box>
        </Stack>

        <Card>
          <UserListToolbar filterName={filterName} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <OrderProductListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const { _id } = row;
                    return (
                      <TableRow hover key={_id} tabIndex={-1}>
                        <TableCell component="th" scope="row">
                          <Typography variant="subtitle2" noWrap>
                            {!row ||
                            !row?.vendorId ||
                            Object.keys(row?.vendorId).length === 0 ||
                            !row?.vendorId?.first_name
                              ? null
                              : row?.vendorId?.first_name}{' '}
                            {!row ||
                            !row?.vendorId ||
                            Object.keys(row?.vendorId).length === 0 ||
                            !row?.vendorId?.last_name
                              ? null
                              : row?.vendorId?.last_name}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          {!row || !row?.vendorId || Object.keys(row?.vendorId).length === 0 || !row?.vendorId?.email
                            ? null
                            : row?.vendorId?.email}
                        </TableCell>
                        <TableCell component="th" scope="row" padding="none">
                          {row?.customProduct
                            ? row?.customProduct.map((product, index) => (
                                <Typography variant="subtitle2" key={index} noWrap p={1}>
                                  {!product ||
                                  !product?.customProductId ||
                                  Object.keys(product?.customProductId).length === 0 ||
                                  !product?.customProductId?.titles
                                    ? 'N/A'
                                    : product?.customProductId?.titles}
                                </Typography>
                              ))
                            : 'N/A'}
                        </TableCell>
                        <TableCell align="left">
                          {row.customProduct
                            ? row.customProduct.map((product, index) => (
                                <Typography variant="body2" key={index} noWrap p={1}>
                                  {!product ||
                                  !product?.customProductId ||
                                  Object.keys(product?.customProductId).length === 0 ||
                                  !product?.customProductId?.sku
                                    ? 'N/A'
                                    : product?.customProductId?.sku}
                                </Typography>
                              ))
                            : 'N/A'}
                        </TableCell>
                        <TableCell align="left">
                          {row.customProduct
                            ? row.customProduct.map((product, index) => (
                                <Typography variant="body2" key={index} noWrap p={1}>
                                  {product?.prices ? product?.prices : 'N/A'}
                                </Typography>
                              ))
                            : 'N/A'}
                        </TableCell>
                        <TableCell align="left">
                          {row?.customProduct
                            ? row.customProduct.map((product, index) => (
                                <Typography variant="body2" key={index} noWrap p={1}>
                                  {product?.cartQuantity ? product?.cartQuantity : 'N/A'}
                                </Typography>
                              ))
                            : 'N/A'}
                        </TableCell>
                        <TableCell align="left">
                          {row.customProduct
                            ? row.customProduct.map((product, index) => (
                                <Typography variant="body2" key={index} noWrap p={1}>
                                  {product?.totalPrice ? product?.totalPrice : 'N/A'}
                                </Typography>
                              ))
                            : 'N/A'}
                        </TableCell>
                        <TableCell align="left">
                          {!row?.customProduct || row?.customProduct.length === 0
                            ? 'N/A'
                            : row?.customProduct.map((product, index) => (
                                <Typography variant="body2" key={index} noWrap p={1}>
                                  {!product?.customFields || Object.keys(product?.customFields).length === 0
                                    ? 'N/A'
                                    : Object.keys(product?.customFields)?.map((key, index) => (
                                        <Typography variant="body2" key={index}>
                                          {camelToSentence(key)}: {product?.customFields[key]?.title}
                                        </Typography>
                                      ))}
                                </Typography>
                              ))}
                        </TableCell>
                        <TableCell align="left">
                          {!row?.customProduct || row?.customProduct.length === 0
                            ? 'N/A'
                            : row?.customProduct.map((product, index) => (
                                <Typography variant="body2" key={index} noWrap p={1}>
                                  {!product?.booleanFields ||
                                  Object.keys(product?.booleanFields).length === 0 ||
                                  !product?.booleanFields?.lamination
                                    ? 'N/A'
                                    : product?.booleanFields?.lamination}
                                </Typography>
                              ))}
                        </TableCell>
                        <TableCell align="left">
                          {!row?.customProduct || row?.customProduct.length === 0
                            ? 'N/A'
                            : row?.customProduct.map((product, index) => (
                                <Typography variant="body2" key={index} noWrap p={1}>
                                  {!product?.booleanFields ||
                                  Object.keys(product?.booleanFields).length === 0 ||
                                  !product?.booleanFields?.mobileModel
                                    ? 'N/A'
                                    : product?.booleanFields?.mobileModel}
                                </Typography>
                              ))}
                        </TableCell>
                        <TableCell align="left">
                          {!row?.customProduct || row?.customProduct.length === 0
                            ? 'N/A'
                            : row?.customProduct.map((product, index) => (
                                <Typography variant="body2" key={index} noWrap p={1}>
                                  {!product?.booleanFields ||
                                  Object.keys(product?.booleanFields).length === 0 ||
                                  !product?.booleanFields?.tissueFilling
                                    ? 'N/A'
                                    : product?.booleanFields?.tissueFilling}
                                </Typography>
                              ))}
                        </TableCell>
                        <TableCell align="left">
                          {!row?.customProduct || row?.customProduct.length === 0
                            ? 'N/A'
                            : row?.customProduct.map((product, index) => (
                                <Typography variant="body2" key={index} noWrap p={1}>
                                  {!product?.booleanFields ||
                                  Object.keys(product?.booleanFields).length === 0 ||
                                  !product?.booleanFields?.files ? (
                                    'N/A'
                                  ) : (
                                    <a href={product?.booleanFields?.files} target="_blank" rel="noreferrer">
                                      View
                                    </a>
                                  )}
                                </Typography>
                              ))}
                        </TableCell>
                        <TableCell>
                          {row.customProduct
                            ? row.customProduct.map((product, index) =>
                                !product ||
                                !product?.customProductId ||
                                Object.keys(product?.customProductId).length === 0 ||
                                !product?.customProductId?.images ||
                                product?.customProductId?.images.length === 0 ? (
                                  'N/A'
                                ) : (
                                  <a href={IMAGE_BASE_URL + product?.customProductId?.images[0]} target="_black">
                                    <img
                                      src={IMAGE_BASE_URL + product?.customProductId?.images[0]}
                                      alt={product?.customProductId?.titles}
                                      key={index}
                                      style={{ width: '100px', padding: '2px' }}
                                    />
                                  </a>
                                )
                              )
                            : 'N/A'}
                        </TableCell>
                        <TableCell align="left">{row?.status}</TableCell>
                        <TableCell align="left">{!row?.disputeDescription ? 'N/A' : row?.disputeDescription}</TableCell>
                        {/* <TableCell align="left">
                          <Link
                            style={{ textDecoration: 'none', color: 'blue' }}
                            to={`/dashboard/vendor-order-details/${_id}`}
                          >
                            View Detail
                          </Link>
                        </TableCell> */}
                        <TableCell align="left">
                          <Button
                            disabled={
                              row?.status === 'Approved' || row?.status === 'Cancelled' || row?.status === 'Return'
                            }
                            // disabled={row.status === 'Cancelled' || row.vendor._id !== userData._id}
                            onClick={(e) => {
                              handleOpenChangeStatusMenu(e, _id, row.status);
                            }}
                          >
                            Change Status
                          </Button>
                        </TableCell>
                        <TableCell align="left">
                          {!row?.customProduct || row?.customProduct.length === 0
                            ? 'N/A'
                            : row?.customProduct.map((product, index) => (
                                <Typography variant="body2" key={index} noWrap p={1}>
                                  {!product?.customProductId ||
                                  Object.keys(product?.customProductId).length === 0 ||
                                  !product?.customProductId?._id ? (
                                    'N/A'
                                  ) : (
                                    <MenuItem
                                      onClick={() => {
                                        navigate(
                                          `/dashboard/custom-product/custom-product-detail/${product?.customProductId?._id}`
                                        );
                                      }}
                                    >
                                      <Iconify icon={'carbon:view'} sx={{ mr: 2 }} />
                                    </MenuItem>
                                  )}
                                </Typography>
                              ))}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph>
                            Not found
                          </Typography>

                          <Typography variant="body2">
                            No results found for &nbsp;
                            <strong>&quot;{filterName}&quot;</strong>.
                            <br /> Try checking for typos or using complete words.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
      <Popover
        open={Boolean(changeStatusMenu)}
        anchorEl={changeStatusMenu}
        onClose={handleCloseChangeStatusMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 200,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        {orderStatus === 'Delivered' || orderStatus === 'Disputed' || orderStatus === 'Return' ? (
          <FormControl fullWidth>
            <Select onChange={handleChangeStatus} sx={{ textAlign: 'left' }}>
              <MenuItem value="Change Status" disabled>
                Change Status
              </MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Disputed">Dispute</MenuItem>
              <MenuItem value="Return">Return Order</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        ) : (
          <FormControl fullWidth>
            <Select onChange={handleChangeStatus} sx={{ textAlign: 'left' }}>
              <MenuItem value="Change Status" disabled>
                Change Status
              </MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Confirm">Confirm</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              {/* <MenuItem value="Cancelled">Cancelled</MenuItem> */}
              <MenuItem value="Delivered">Deliverd</MenuItem>
            </Select>
          </FormControl>
        )}
      </Popover>

      <CustomPopup open={isDispute} setOpen={setIsDispute}>
        <Stack spacing={2} padding={4} sx={{ width: '500px' }}>
          <Typography variant="h5">Reason</Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            name="disputeDescription"
            type="text"
            label="Description"
            onChange={(e) => setDisputeDescription(e.target.value)}
          />
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" onClick={() => handleDisputed()}>
              Submit
            </Button>
          </Box>
        </Stack>
      </CustomPopup>
    </>
  );
}
